<template>
  <div>
    <template v-if="!consolePreviewMode">
      <legal-documents v-if="!webViewMode" acceptance-mode @log-out="logOutUser" />
      <email-confirmation-dialog :show-dialog="showEmailConfirmationDialog" @close="showEmailConfirmationDialog = false" />
      <paywall-success-dialog v-if="paywallSuccessDialogDisplayed" @close="shouldDisplayPaywallSuccessDialog = false" />
    </template>

    <message-dialog v-if="sessionDialogDisplayed" :dialog="sessionDialogDisplayed" @cancel="toggleSessionDialog(false)" @ok="logOutUser">
      <template #title>{{ $translate("EventsHub.dialogHeader") }}</template>
      <template #content>{{ $translate("EventsHub.dialogContent") }}</template>
      <template #cancel>{{ $translate("common.cancel") }}</template>
      <template #ok>{{ $translate("common.logOut") }}</template>
    </message-dialog>

    <message-dialog
      v-if="Boolean(groupToJoinKey)"
      :dialog="Boolean(groupToJoinKey)"
      :ok-button-loading="joiningGroup"
      persistent
      @ok="joinPostGroup"
      @cancel="groupToJoinKey = null"
    >
      <template #content>{{ $translate("EventsHub.youNeedToBePartOfGroup") }}</template>
      <template #cancel>{{ $translate("common.cancel") }}</template>
      <template #ok>{{ $translate("common.join") }}</template>
    </message-dialog>
  </div>
</template>

<script>
import { mapActions, mapState } from "pinia"

import { useAppStateStore } from "@/store/appState.js"
import { useUserSessionStore } from "@/store/userSession.js"
import { useGroupsStore } from "@/store/groups.js"

import LegalDocuments from "@/components/LegalDocuments.vue"
import EmailConfirmationDialog from "@/components/EmailConfirmationDialog.vue"
import PaywallSuccessDialog from "@/components/premium/PaywallSuccessDialog.vue"

import { registerAnalyticsEvent } from "@/services/analytics.js"
import * as authorization from "@/services/authorization.js"
import * as paywallService from "@/services/paywallService.js"
import { isIos, isSafari } from "@/services/environment.js"

import { usePubNubSubscriptions } from "@/composables/usePubNubSubscriptions.js"

import debounce from "lodash/debounce"

export default {
  components: {
    LegalDocuments,
    EmailConfirmationDialog,
    PaywallSuccessDialog
  },
  setup() {
    const { subscribeToChannel, unsubscribeFromChannel } = usePubNubSubscriptions()
    return { subscribeToChannel, unsubscribeFromChannel }
  },
  data() {
    return {
      sessionDialogDisplayed: false,
      showEmailConfirmationDialog: false,
      shouldDisplayPaywallSuccessDialog: false,
      waitingForAvatarChange: false,
      groupToJoinKey: null,
      joiningGroup: false,
      previousWindowWidth: 0,
      previousWindowHeight: 0,
      previousViewportHeight: 0,
      debounceResizeEvent: debounce(this.broadcastWindowResizeEvent, 500),
      isIos: isIos(),
      isSafari: isSafari()
    }
  },
  computed: {
    ...mapState(useAppStateStore, ["webViewMode"]),
    ...mapState(useUserSessionStore, ["user", "ownedPaywallPlan", "paywallData", "consolePreviewMode"]),
    ...mapState(useGroupsStore, ["groupForKey"]),
    groupToJoin() {
      if (!this.groupToJoinKey) {
        return null
      }
      return this.groupForKey(this.groupToJoinKey)
    },
    paywallSuccessDialogDisplayed() {
      return this.ownedPaywallPlan && this.shouldDisplayPaywallSuccessDialog
    }
  },
  watch: {
    $route() {
      this.showEmailConfirmationDialog = false
    },
    paywallSuccessDialogDisplayed(val) {
      document.documentElement.style.overflowY = val ? "hidden" : null
    }
  },
  created() {
    this.$eventBus.$on("log-out-user", this.onLogOutUser)
    this.$eventBus.$on("unauthorized-access", this.logOutUser)
    this.$eventBus.$on("refresh-session-error", this.logOutUserAfterRefreshError)
    this.$eventBus.$on("pn-notification-counts", this.setCounters)
    this.$eventBus.$on("email-confirmation-required", this.toggleEmailDialog)
    this.$eventBus.$on("request-paywall", this.onRequestPaywall)
    this.$eventBus.$on("listen-for-avatar-change", this.onAvatarChange)
    this.$eventBus.$on("check-post-auth", this.checkPostAuth)

    window.addEventListener("appinstalled", this.onAppInstalled)
    window.addEventListener("popstate", this.deleteCommentLinkWhenComingBack)
    window.addEventListener("pageshow", this.invalidatePageCacheSafari)
  },
  mounted() {
    window.addEventListener("resize", this.debounceResizeEvent)
    window.addEventListener("scroll", this.broadcastWindowScrollEvent)
    if (this.isIos && window.visualViewport) {
      window.visualViewport.addEventListener("resize", this.broadcastViewportResizeEvent)
    }

    if (screen.orientation) {
      screen.orientation.addEventListener("change", this.broadcastOrientationChangeEvent)
    }

    if (this.$routerState.wasSubscriptionSuccess) {
      this.shouldDisplayPaywallSuccessDialog = true
    }

    if (this.$routerState.pnId) {
      registerAnalyticsEvent({
        name: "notification-opened",
        attributes: {
          push_notification_id: this.$routerState.pnId
        }
      })
    }

    this.saveWindowDimensions()
    if (this.isIos) {
      this.saveViewportDimensions()
    }
  },
  beforeUnmount() {
    window.removeEventListener("resize", this.debounceResizeEvent)
    window.removeEventListener("scroll", this.broadcastWindowScrollEvent)
    window.removeEventListener("appinstalled", this.onAppInstalled)
    window.removeEventListener("popstate", this.deleteCommentLinkWhenComingBack)
    window.removeEventListener("pageshow", this.invalidatePageCacheSafari)

    if (this.isIos && window.visualViewport) {
      window.visualViewport.removeEventListener("resize", this.broadcastViewportResizeEvent)
    }

    if (screen.orientation) {
      screen.orientation.removeEventListener("change", this.broadcastOrientationChangeEvent)
    }

    this.$eventBus.$off("log-out-user", this.onLogOutUser)
    this.$eventBus.$off("unauthorized-access", this.logOutUser)
    this.$eventBus.$off("refresh-session-error", this.logOutUserAfterRefreshError)
    this.$eventBus.$off("pn-notification-counts", this.setCounters)
    this.$eventBus.$off("email-confirmation-required", this.toggleEmailDialog)
    this.$eventBus.$off("request-paywall", this.onRequestPaywall)
    this.$eventBus.$off("listen-for-avatar-change", this.onAvatarChange)
    this.$eventBus.$off("check-post-auth", this.checkPostAuth)
  },
  methods: {
    ...mapActions(useUserSessionStore, ["setUserData", "setCounters", "fetchPaywallData"]),
    ...mapActions(useGroupsStore, ["joinGroup"]),
    invalidatePageCacheSafari(event) {
      // https://stackoverflow.com/a/13123626
      if ((this.isIos || this.isSafari) && event.persisted) {
        window.location.reload()
      }
    },
    deleteCommentLinkWhenComingBack() {
      //Needed when we click back quickly, for some reason it is not updated immediately
      setTimeout(() => {
        if (this.$route.name === this.$routes.COMMENT) {
          this.$router.replace({
            name: this.$routes.POST,
            params: {
              postId: Number(this.$route.params.postId)
            }
          })
        }
      })
    },
    saveWindowDimensions() {
      this.previousWindowWidth = window.innerWidth
      this.previousWindowHeight = window.innerHeight
    },
    saveViewportDimensions() {
      this.previousViewportHeight = window.visualViewport?.height
    },
    broadcastWindowResizeEvent() {
      this.$eventBus.$emit("window-resize", {
        widthChanged: this.previousWindowWidth !== window.innerWidth,
        heightChanged: this.previousWindowHeight !== window.innerHeight
      })
      this.saveWindowDimensions()
    },
    broadcastViewportResizeEvent() {
      this.$eventBus.$emit("viewport-resize")
      this.saveViewportDimensions()
    },
    broadcastWindowScrollEvent() {
      this.$eventBus.$emit("window-scroll")
    },
    broadcastOrientationChangeEvent() {
      this.$eventBus.$emit("orientation-change")
    },
    toggleEmailDialog(value) {
      this.showEmailConfirmationDialog = value
    },
    async onRequestPaywall(requiredSubscriptionData) {
      if (!this.paywallData) {
        await this.fetchPaywallData()
      }

      const pushOptions = { name: this.$routes.PAYWALL }

      if (requiredSubscriptionData.relatedPlans.length) {
        pushOptions.query = {
          "required-plans-names": requiredSubscriptionData.relatedPlans.map((p) => p.name).join(",")
        }

        const { sourceId, sourceType, routeParamId } = requiredSubscriptionData
        paywallService.registerLastSource({ sourceId, sourceType, routeParamId: routeParamId || sourceId })
      }

      this.$router.push(pushOptions)
    },
    checkPostAuth({ post, callback }) {
      const { groupToJoinKey } = authorization.checkPostInteractionAuth({ post, callback, route: this.$route })
      this.groupToJoinKey = groupToJoinKey
    },
    async joinPostGroup() {
      try {
        this.joiningGroup = true
        await this.joinGroup(this.groupToJoin)

        this.$eventBus.$emit("group-join", this.groupToJoin)
      } catch (err) {
        this.$reportError(err, this.$translate("common.somethingWrong"))
      } finally {
        this.joiningGroup = false
        this.groupToJoinKey = null
      }
    },
    onAvatarChange() {
      if (this.waitingForAvatarChange) {
        return
      }

      this.waitingForAvatarChange = true
      this.$eventBus.$on("pn-avatar-published", () => {
        this.setUserData()
        this.$eventBus.$off("pn-avatar-published")
        this.unsubscribeFromChannel(`users.${this.user.id}`)
        this.waitingForAvatarChange = false
      })

      this.subscribeToChannel(`users.${this.user.id}`)
    },
    onAppInstalled() {
      registerAnalyticsEvent({
        name: "pwa-install",
        attributes: {
          user_agent: navigator.userAgent
        }
      })
    },
    toggleSessionDialog(value) {
      this.sessionDialogDisplayed = value
    },
    onLogOutUser({ immediate, dontCallApi } = {}) {
      if (immediate) {
        this.logOutUser({ dontCallApi })
      } else {
        this.toggleSessionDialog(true)
      }
    },
    logOutUser({ dontCallApi } = {}) {
      this.toggleSessionDialog(false)
      this.$emit("log-out", { dontCallApi })
    },
    logOutUserAfterRefreshError() {
      this.logOutUser({ dontCallApi: true })
    }
  }
}
</script>
